import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Self-service password reset — no login required. Firebase emails a reset
// link directly to the address if an account exists; we always show the
// same success message regardless of whether the email matched an account,
// so this can't be used to check who has an account here (same principle
// as checkExistingClient's fix).
const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const { forgotPassword } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await forgotPassword(email);
    } catch (err) {
      // Firebase throws auth/user-not-found for unregistered emails — don't
      // reveal that distinction, just show the same generic success state.
    } finally {
      setLoading(false);
      setSent(true);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#f0f4f6' }}>
      <nav className="flex items-center justify-between px-8 py-5" style={{ backgroundColor: '#1e562d' }}>
        <Link to="/" className="text-white font-bold text-lg tracking-wide">
          Shane Bruce Mental Health &amp; Coaching
        </Link>
        <Link to="/login"
              className="text-sm font-medium px-4 py-2 rounded-lg border border-white text-white hover:bg-white hover:text-teal-800 transition-colors">
          Sign In
        </Link>
      </nav>

      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="bg-white rounded-2xl shadow-md w-full max-w-md p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold mb-1" style={{ color: '#1e562d' }}>
              Reset Your Password
            </h1>
            <p className="text-sm text-gray-500">
              Remembered it? <Link to="/login" className="font-medium hover:underline" style={{ color: '#1e562d' }}>Sign in</Link>
            </p>
          </div>

          {error && (
            <div className="mb-5 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}

          {sent ? (
            <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-green-800 text-sm">
              If an account exists for that email, we've sent a link to reset your password. Check your inbox — and your spam or junk folder if it doesn't show up in a minute.
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Email Address
                </label>
                <input
                  type="email" autoComplete="email" required
                  placeholder="you@email.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                />
              </div>

              <button
                type="submit" disabled={loading}
                className="w-full py-3 rounded-xl text-white font-semibold text-sm mt-2 hover:opacity-90 transition-opacity disabled:opacity-60"
                style={{ backgroundColor: '#1e562d' }}
              >
                {loading ? 'Sending…' : 'Send Reset Link →'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
